// Animated force-directed graph of the tenant's configuration.
//
// Hand-rolled rather than pulling in d3-force or a graph library. The
// simulation below is ~60 lines, the console already avoids third-party
// requests on principle (fonts are self-hosted for the same reason), and the
// bundle is over 500kB without help. A library would also fight the design
// tokens; rendering SVG ourselves means the graph is themed like everything
// else, in both light and dark, for free.
//
// The physics is deliberately the simple form: O(n^2) repulsion between every
// pair, springs along edges, and a pull toward the centre. Barnes-Hut would
// matter in the thousands of nodes; a tenant's configuration is tens.

import * as React from "react";

import type { GraphEdge, GraphNode, GraphResponse } from "@/types/api";

interface Simulated extends GraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Pinned by dragging — the simulation stops moving it. */
  fixed?: boolean;
}

export type Shape =
  | "circle"
  | "ring"
  | "square"
  | "document"
  | "triangle"
  | "triangle-down"
  | "diamond"
  | "pentagon"
  | "hexagon"
  | "octagon"
  | "star"
  | "cross";

interface KindStyle {
  fill: string;
  r: number;
  /** Shape says *what* a thing is — an agent is a circle, a skill a triangle,
   *  wherever it lives. Colour says *whose* it is. */
  shape: Shape;
  /** Which half of the canvas it settles in: Aviary's own configuration on the
   *  left, GitHub repositories on the right. The organisation sits between. */
  side?: "left" | "right";
}

/** Per-kind appearance. Kinds are a closed set from the API. */
const STYLE: Record<string, KindStyle> = {
  tenant: { fill: "var(--graph-tenant)", r: 22, shape: "hexagon" },
  domain: { fill: "var(--graph-domain)", r: 15, shape: "square", side: "left" },
  agent: { fill: "var(--graph-agent)", r: 14, shape: "circle", side: "left" },
  skill: { fill: "var(--graph-skill)", r: 10, shape: "triangle", side: "left" },
  tool: { fill: "var(--graph-tool)", r: 10, shape: "diamond", side: "left" },
  knowledge_base: { fill: "var(--graph-kb)", r: 11, shape: "pentagon", side: "left" },
  llm_model: { fill: "var(--graph-model)", r: 12, shape: "star", side: "left" },
  trusted_system: { fill: "var(--graph-trusted)", r: 10, shape: "cross", side: "left" },
  github_repo: { fill: "var(--graph-repo)", r: 15, shape: "octagon", side: "right" },
  claude_code: { fill: "var(--graph-claude)", r: 12, shape: "ring", side: "right" },
  cursor: { fill: "var(--graph-cursor)", r: 12, shape: "ring", side: "right" },
  repo_agent: { fill: "var(--graph-claude)", r: 11, shape: "circle", side: "right" },
  repo_skill: { fill: "var(--graph-claude)", r: 10, shape: "triangle", side: "right" },
  repo_command: { fill: "var(--graph-claude)", r: 10, shape: "triangle-down", side: "right" },
  repo_rule: { fill: "var(--graph-cursor)", r: 10, shape: "document", side: "right" },
  cursor_agent: { fill: "var(--graph-cursor)", r: 11, shape: "circle", side: "right" },
  cursor_skill: { fill: "var(--graph-cursor)", r: 10, shape: "triangle", side: "right" },
  cursor_command: { fill: "var(--graph-cursor)", r: 10, shape: "triangle-down", side: "right" },
};
const FALLBACK: KindStyle = { fill: "var(--graph-domain)", r: 10, shape: "circle" };

/** Legend order, and the name of each kind. */
export const KIND_LABELS: Record<string, string> = {
  tenant: "Organisation",
  domain: "Domain",
  agent: "Agent",
  skill: "Skill",
  tool: "Tool",
  knowledge_base: "Knowledge base",
  llm_model: "LLM provider",
  trusted_system: "Trusted system",
  github_repo: "GitHub repo",
  claude_code: "Claude Code",
  cursor: "Cursor",
  repo_agent: "Claude agent",
  repo_skill: "Claude skill",
  repo_command: "Claude command",
  repo_rule: "Cursor rule",
  cursor_agent: "Cursor agent",
  cursor_skill: "Cursor skill",
  cursor_command: "Cursor command",
};

export function styleFor(kind: string): KindStyle {
  return STYLE[kind] ?? FALLBACK;
}

/** Found in a GitHub repository rather than configured in Aviary. */
export const isRepoSide = (kind: string) => styleFor(kind).side === "right";

const WIDTH = 900;
const HEIGHT = 520;

function polygon(sides: number, r: number, rotation = -Math.PI / 2): string {
  return Array.from({ length: sides }, (_, i) => {
    const a = rotation + (i * 2 * Math.PI) / sides;
    return `${(Math.cos(a) * r).toFixed(2)},${(Math.sin(a) * r).toFixed(2)}`;
  }).join(" ");
}

function star(r: number): string {
  return Array.from({ length: 10 }, (_, i) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const d = i % 2 === 0 ? r : r * 0.45;
    return `${(Math.cos(a) * d).toFixed(2)},${(Math.sin(a) * d).toFixed(2)}`;
  }).join(" ");
}

function cross(r: number): string {
  const a = r;
  const b = r * 0.36;
  return [
    [-b, -a],
    [b, -a],
    [b, -b],
    [a, -b],
    [a, b],
    [b, b],
    [b, a],
    [-b, a],
    [-b, b],
    [-a, b],
    [-a, -b],
    [-b, -b],
  ]
    .map(([x, y]) => `${x!.toFixed(2)},${y!.toFixed(2)}`)
    .join(" ");
}

interface Paint {
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  strokeDasharray?: string;
  className?: string;
}

/** One node's shape, centred on the origin. Also used by the legend. */
export function NodeShape({ shape, r, ...paint }: { shape: Shape; r: number } & Paint) {
  switch (shape) {
    case "circle":
      return <circle r={r} {...paint} />;
    case "ring": {
      // A donut: the hole is real (even-odd), so edges show through it.
      const i = r * 0.5;
      const d = `M ${r} 0 A ${r} ${r} 0 1 0 ${-r} 0 A ${r} ${r} 0 1 0 ${r} 0 Z M ${i} 0 A ${i} ${i} 0 1 1 ${-i} 0 A ${i} ${i} 0 1 1 ${i} 0 Z`;
      return <path d={d} fillRule="evenodd" {...paint} />;
    }
    case "square": {
      const s = r * 0.88;
      return <rect x={-s} y={-s} width={s * 2} height={s * 2} rx={1.5} {...paint} />;
    }
    case "document":
      return (
        <rect x={-r * 1.15} y={-r * 0.75} width={r * 2.3} height={r * 1.5} rx={2} {...paint} />
      );
    case "triangle":
      return <polygon points={polygon(3, r * 1.2)} {...paint} />;
    case "triangle-down":
      return <polygon points={polygon(3, r * 1.2, Math.PI / 2)} {...paint} />;
    case "diamond":
      return <polygon points={polygon(4, r * 1.2)} {...paint} />;
    case "pentagon":
      return <polygon points={polygon(5, r * 1.08)} {...paint} />;
    case "hexagon":
      return <polygon points={polygon(6, r, 0)} {...paint} />;
    case "octagon":
      return <polygon points={polygon(8, r * 1.05, Math.PI / 8)} {...paint} />;
    case "star":
      return <polygon points={star(r * 1.25)} {...paint} />;
    case "cross":
      return <polygon points={cross(r * 1.05)} {...paint} />;
  }
}

export function ForceGraph({
  data,
  onSelect,
  selected,
  className = "h-[520px] w-full",
}: {
  data: GraphResponse;
  onSelect: (node: GraphNode | null) => void;
  selected: string | null;
  /** Size of the rendered SVG. The layout is computed on a fixed canvas and
   *  scaled to fit, so a larger box (the expanded view) just draws it bigger. */
  className?: string;
}) {
  const [nodes, setNodes] = React.useState<Simulated[]>([]);
  const [, setTick] = React.useState(0);
  const nodesRef = React.useRef<Simulated[]>([]);
  const dragging = React.useRef<string | null>(null);
  const svgRef = React.useRef<SVGSVGElement>(null);
  const settled = React.useRef(false);
  const wakeRef = React.useRef<() => void>(() => {});

  // With no repositories there is no right-hand side, and Aviary's own graph
  // keeps the whole canvas as before.
  const hasRepos = React.useMemo(() => data.nodes.some((n) => isRepoSide(n.kind)), [data]);
  const targetX = React.useCallback(
    (kind: string) => {
      const side = styleFor(kind).side;
      if (!hasRepos || !side) return WIDTH / 2;
      return side === "left" ? WIDTH * 0.3 : WIDTH * 0.73;
    },
    [hasRepos],
  );

  // The simulation's heat, kept across restarts. A refetch that adds nothing
  // continues at whatever heat the layout had — still settling if it was, at
  // rest if it was — instead of starting over. Resetting it to "barely warm"
  // was tried and froze a layout that was still settling when the cached data
  // was replaced a moment after load.
  const alphaRef = React.useRef(1);

  // Seed positions on a circle around each node's side rather than at random:
  // a random cloud takes noticeably longer to untangle and looks like it is
  // thrashing while it does.
  //
  // A refetch must not throw the layout away. The dashboard refetches when the
  // window regains focus, and re-seeding every node made the whole map jump
  // each time you came back to the tab — moving a node out from under the
  // click aimed at it. Nodes still present keep where they were (and whether
  // they were pinned); only new ones are seeded.
  React.useEffect(() => {
    const previous = new Map(nodesRef.current.map((n) => [n.id, n]));
    let added = 0;
    const seeded: Simulated[] = data.nodes.map((n, i) => {
      const kept = previous.get(n.id);
      if (kept) return { ...n, x: kept.x, y: kept.y, vx: 0, vy: 0, fixed: kept.fixed };
      added += 1;
      const angle = (i / Math.max(1, data.nodes.length)) * Math.PI * 2;
      const radius = n.kind === "tenant" ? 0 : hasRepos ? 120 : 180;
      return {
        ...n,
        x: targetX(n.kind) + Math.cos(angle) * radius,
        y: HEIGHT / 2 + Math.sin(angle) * radius,
        vx: 0,
        vy: 0,
      };
    });
    // Only something new needs the layout re-heated.
    if (previous.size === 0 || added > 0) alphaRef.current = 1;
    nodesRef.current = seeded;
    setNodes(seeded);
  }, [data, hasRepos, targetX]);

  // The simulation. Runs on rAF and settles itself: velocity is damped every
  // frame, so it comes to rest instead of jittering forever burning CPU on a
  // dashboard someone leaves open.
  React.useEffect(() => {
    let frame = 0;
    let alpha = alphaRef.current;
    const byId = new Map<string, Simulated>();

    const step = () => {
      const ns = nodesRef.current;
      if (ns.length === 0) {
        frame = requestAnimationFrame(step);
        return;
      }
      byId.clear();
      for (const n of ns) byId.set(n.id, n);

      // Repulsion: every pair pushes apart, falling off with distance.
      for (let i = 0; i < ns.length; i++) {
        for (let j = i + 1; j < ns.length; j++) {
          // Indices are in range by construction; the assertion is for
          // noUncheckedIndexedAccess, not a real possibility.
          const a = ns[i]!;
          const b = ns[j]!;
          let dx = b.x - a.x;
          let dy = b.y - a.y;
          let dist = Math.hypot(dx, dy);
          if (dist < 1) {
            // Two nodes exactly on top of each other have no direction to
            // separate along; nudge them apart deterministically.
            dx = (i - j) * 0.5;
            dy = 0.5;
            dist = 1;
          }
          const force = 2600 / (dist * dist);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          a.vx -= fx;
          a.vy -= fy;
          b.vx += fx;
          b.vy += fy;
        }
      }

      // Springs along edges.
      for (const e of data.edges) {
        const a = byId.get(e.source);
        const b = byId.get(e.target);
        if (!a || !b) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 1;
        const force = (dist - 110) * 0.015;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        a.vx += fx;
        a.vy += fy;
        b.vx -= fx;
        b.vy -= fy;
      }

      // Gravity toward each node's side (the centre when there is only one), so
      // disconnected pieces don't drift away and Aviary / GitHub stay apart.
      // The sideways pull is stronger than the vertical one: it is what keeps
      // the two halves legible when springs run between them.
      for (const n of ns) {
        const sided = hasRepos && styleFor(n.kind).side !== undefined;
        // 0.012 packed the Aviary side tightly enough that labels collided.
        n.vx += (targetX(n.kind) - n.x) * (sided ? 0.007 : 0.004);
        n.vy += (HEIGHT / 2 - n.y) * 0.004;
      }

      for (const n of ns) {
        if (n.fixed || n.id === dragging.current) {
          n.vx = 0;
          n.vy = 0;
          continue;
        }
        n.vx *= 0.82;
        n.vy *= 0.82;
        n.x += n.vx * alpha;
        n.y += n.vy * alpha;
        // Keep everything on canvas.
        n.x = Math.max(30, Math.min(WIDTH - 30, n.x));
        n.y = Math.max(30, Math.min(HEIGHT - 30, n.y));
      }

      alpha *= 0.985;
      alphaRef.current = alpha;

      // Stop, and stop for certain. The first version floored alpha at 0.02, so
      // gravity kept nudging everything forever: the layout drifted under the
      // cursor and a node you aimed at had moved by the time you clicked,
      // besides running rAF for the life of the page.
      //
      // Two conditions, because one is not enough. Residual forces never quite
      // cancel, so a pure energy threshold can hover above the line
      // indefinitely — the first attempt at this used 0.4 and never tripped.
      // The alpha floor is the guarantee: positions move by `v * alpha`, so
      // once alpha is small the layout is visually stationary regardless of
      // what the velocities are doing.
      const energy = ns.reduce((sum, n) => sum + Math.abs(n.vx) + Math.abs(n.vy), 0);
      setTick((t) => t + 1);
      if (!dragging.current && (alpha < 0.05 || energy < 2)) {
        settled.current = true;
        return;
      }
      frame = requestAnimationFrame(step);
    };

    // Dragging re-heats the simulation so the rest of the graph responds, and
    // a settled loop has to be restarted because it stopped asking for frames.
    const wake = () => {
      if (!settled.current) return;
      settled.current = false;
      alpha = Math.max(alpha, 0.35);
      alphaRef.current = alpha;
      frame = requestAnimationFrame(step);
    };
    wakeRef.current = wake;

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [data, hasRepos, targetX]);

  const point = (e: React.PointerEvent) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * WIDTH,
      y: ((e.clientY - rect.top) / rect.height) * HEIGHT,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const p = point(e);
    if (!p) return;
    const n = nodesRef.current.find((x) => x.id === dragging.current);
    if (n) {
      n.x = p.x;
      n.y = p.y;
      wakeRef.current();
    }
  };

  // Neighbours of the selection, for dimming everything else.
  const related = React.useMemo(() => {
    if (!selected) return null;
    const set = new Set<string>([selected]);
    for (const e of data.edges) {
      if (e.source === selected) set.add(e.target);
      if (e.target === selected) set.add(e.source);
    }
    return set;
  }, [selected, data.edges]);

  const live = nodesRef.current.length ? nodesRef.current : nodes;
  const positions = new Map(live.map((n) => [n.id, n]));

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className={`${className} touch-none select-none`}
      onPointerMove={onPointerMove}
      onPointerUp={() => (dragging.current = null)}
      onPointerLeave={() => (dragging.current = null)}
      onClick={() => onSelect(null)}
    >
      {hasRepos ? (
        <g className="pointer-events-none fill-muted-foreground text-[11px] uppercase tracking-wider">
          <text x={WIDTH * 0.3} y={18} textAnchor="middle">
            Aviary
          </text>
          <text x={WIDTH * 0.73} y={18} textAnchor="middle">
            GitHub repositories
          </text>
        </g>
      ) : null}
      <g>
        {data.edges.map((e: GraphEdge, i) => {
          const a = positions.get(e.source);
          const b = positions.get(e.target);
          if (!a || !b) return null;
          const dim = related && !(related.has(e.source) && related.has(e.target));
          return (
            <line
              key={i}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke="currentColor"
              className="text-border"
              strokeWidth={1}
              opacity={dim ? 0.12 : 0.55}
            />
          );
        })}
      </g>
      <g>
        {live.map((n) => {
          const s = styleFor(n.kind);
          const dim = related && !related.has(n.id);
          const isSelected = selected === n.id;
          return (
            <g
              key={n.id}
              transform={`translate(${n.x} ${n.y})`}
              opacity={dim ? 0.2 : 1}
              className="cursor-pointer"
              onPointerDown={(e) => {
                e.stopPropagation();
                dragging.current = n.id;
                wakeRef.current();
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(n);
              }}
            >
              {/* A solid hit target under every shape. The ring's hole is
                  real (even-odd, so edges show through it), and without this a
                  click on the middle of a Claude Code or Cursor node fell
                  through to the canvas — which deselects. */}
              <circle r={s.r * 1.3} fill="transparent" />
              <NodeShape
                shape={s.shape}
                r={s.r}
                fill={s.fill}
                stroke="currentColor"
                // Solid for every kind. A dashed outline was tried for the
                // GitHub side and blurred small shapes into gears; side,
                // colour and the heading already say where a node lives.
                className={isSelected ? "text-foreground" : "text-background"}
                strokeWidth={isSelected ? 3 : 1.5}
              />
              <text
                y={s.r * 1.25 + 11}
                textAnchor="middle"
                className="pointer-events-none fill-foreground text-[10px]"
              >
                {n.label.length > 22 ? `${n.label.slice(0, 21)}…` : n.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}
